import React, { useState } from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { cn } from '../lib/utils'
import {
  CheckCircle,
  Twitter,
  Linkedin,
  Copy,
  Gift,
  X,
  Share2
} from 'lucide-react'

interface ShareSuccessProps {
  siteUrl: string
  languages: string[]
  onClose?: () => void
}

const ShareSuccess: React.FC<ShareSuccessProps> = ({ siteUrl, languages, onClose }) => {
  const [copied, setCopied] = useState(false)
  const [messageCopied, setMessageCopied] = useState(false)

  const referralCode = 'USER123' // This would come from user context
  const shareText = `Just made my website multilingual with LocalizationHub! Now supporting ${languages.join(', ')} languages with just one line of code. Check it out: ${siteUrl}`
  const shareUrl = `https://localizationhub.com/?ref=${referralCode}`

  const copyToClipboard = async (text: string, isMessage = false) => {
    try {
      await navigator.clipboard.writeText(text)
      if (isMessage) {
        setMessageCopied(true)
        setTimeout(() => setMessageCopied(false), 2000)
      } else {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const shareOnTwitter = () => {
    const tweetText = encodeURIComponent(shareText)
    const tweetUrl = encodeURIComponent(shareUrl)
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=${tweetUrl}`, '_blank')
  }

  const shareOnLinkedIn = () => {
    const linkedInUrl = encodeURIComponent(shareUrl)
    const linkedInText = encodeURIComponent(shareText)
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${linkedInUrl}&summary=${linkedInText}`, '_blank')
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20">
      <Card className="w-full max-w-md mx-4 relative">
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-xl">Your site is now multilingual!</CardTitle>
          <CardDescription>
            Your website now supports{' '}
            <span className="font-medium text-foreground">{languages.join(', ')}</span>{' '}
            languages. Share your success!
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Benefits of sharing */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Gift className="h-4 w-4 text-primary mr-2" />
              <h4 className="font-medium text-foreground text-sm">Share & Earn Benefits</h4>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                Get 500 extra translations when friends sign up
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                Unlock priority support for active referrers
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                Build your international presence
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                Help make the web more accessible
              </li>
            </ul>
          </div>

          {/* Share buttons */}
          <div className="space-y-3">
            <Button
              onClick={shareOnTwitter}
              className="w-full bg-[#1DA1F2] hover:bg-[#1a8cd8]"
            >
              <Twitter className="h-4 w-4 mr-2" />
              Share on Twitter
            </Button>

            <Button
              onClick={shareOnLinkedIn}
              className="w-full bg-[#0A66C2] hover:bg-[#094f94]"
            >
              <Linkedin className="h-4 w-4 mr-2" />
              Share on LinkedIn
            </Button>
          </div>

          {/* Referral link */}
          <div className="pt-3 border-t space-y-2">
            <Label className="text-sm">Or copy your referral link:</Label>
            <div className="flex">
              <Input
                type="text"
                value={shareUrl}
                readOnly
                className="rounded-r-none bg-muted/50 text-sm"
              />
              <Button
                onClick={() => copyToClipboard(shareUrl)}
                variant={copied ? 'default' : 'secondary'}
                className={cn(
                  "rounded-l-none",
                  copied && "bg-green-500 hover:bg-green-600"
                )}
              >
                <Copy className="h-4 w-4 mr-1" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => copyToClipboard(shareText, true)}
              className={cn(
                "flex-1",
                messageCopied && "bg-green-500 hover:bg-green-600"
              )}
            >
              <Share2 className="h-4 w-4 mr-2" />
              {messageCopied ? 'Copied!' : 'Copy Message'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ShareSuccess
